import axios from "axios";
import {
  triggerGlobalLoadingStart,
  triggerGlobalLoadingStop,
} from "../context/LoadingContext";

export const AUTH_TOKEN_KEY = "authToken";

const apiClient = axios.create({
  baseURL:
    process.env.REACT_APP_API_BASE_URL || "https://api.qa.techapurv.com/users",
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const isFinance =
      Boolean(config.isFinance) ||
      (typeof config.url === "string" &&
        (config.url.startsWith("/finance") || config.url.includes("/finance"))) ||
      (typeof config.baseURL === "string" && config.baseURL.includes("/finance"));

    if (isFinance) {
      // Direct /finance requests to the finance service host rather than default /users base URL
      let financeHost = process.env.REACT_APP_FINANCE_API_BASE_URL;
      if (financeHost) {
        financeHost = financeHost.replace(/\/finance(\/api)?\/?$/, "");
      } else if (process.env.REACT_APP_API_BASE_URL) {
        financeHost = process.env.REACT_APP_API_BASE_URL.replace(/\/users\/?$/, "");
      } else {
        financeHost = "https://api.qa.techapurv.com";
      }
      config.baseURL = financeHost;
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      if (typeof config.headers?.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
      } else {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Clear session on 401 if it's NOT a dev token
    if (error.response?.status === 401) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const isDevToken =
        token &&
        (token.startsWith("dev-token-") || token.startsWith("mock-token-"));
      if (!isDevToken) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem("user");
      }
    }

    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected API error occurred.";

    return Promise.reject(Object.assign(error, { userMessage: message }));
  },
);

export const FINANCE_API_BASE_URL =
  process.env.REACT_APP_FINANCE_API_BASE_URL ||
  (process.env.REACT_APP_API_BASE_URL
    ? process.env.REACT_APP_API_BASE_URL.replace(/\/users\/?$/, "/finance/api")
    : "https://api.qa.techapurv.com/finance/api");

export const financeApiClient = axios.create({
  baseURL: FINANCE_API_BASE_URL,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

financeApiClient.interceptors.request.use(
  (config) => {
    // Strip duplicate /finance/api or /finance prefix from url when using financeApiClient
    if (typeof config.url === "string") {
      if (config.url.startsWith("/finance/api")) {
        config.url = config.url.replace(/^\/finance\/api/, "");
      } else if (config.url.startsWith("/finance")) {
        config.url = config.url.replace(/^\/finance/, "");
      }
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      if (typeof config.headers?.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
      } else {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

financeApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const isDevToken =
        token &&
        (token.startsWith("dev-token-") || token.startsWith("mock-token-"));
      if (!isDevToken) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem("user");
      }
    }

    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected API error occurred.";

    return Promise.reject(Object.assign(error, { userMessage: message }));
  },
);

export const LEAVE_API_BASE_URL =
  process.env.REACT_APP_LEAVE_API_BASE_URL ||
  (process.env.REACT_APP_API_BASE_URL
    ? (process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "").endsWith("/api")
        ? process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "")
        : `${process.env.REACT_APP_API_BASE_URL.replace(/\/+$/, "")}${
            process.env.REACT_APP_API_BASE_URL.includes("/users") ? "" : "/users"
          }/api`)
    : "https://api.qa.techapurv.com/users/api");

export const leaveApiClient = axios.create({
  baseURL: LEAVE_API_BASE_URL,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

leaveApiClient.interceptors.request.use(
  (config) => {
    if (typeof config.url === "string") {
      // Strip duplicate /users/api or /api prefix from url when using leaveApiClient
      if (config.url.startsWith("/users/api")) {
        config.url = config.url.replace(/^\/users\/api/, "");
      } else if (config.url.startsWith("/api")) {
        config.url = config.url.replace(/^\/api/, "");
      }
    }

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      if (typeof config.headers?.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
      } else {
        if (!config.headers) config.headers = {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

leaveApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const isDevToken =
        token &&
        (token.startsWith("dev-token-") || token.startsWith("mock-token-"));
      if (!isDevToken) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem("user");
      }
    }

    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected API error occurred.";

    return Promise.reject(Object.assign(error, { userMessage: message }));
  },
);


// Attach global loading tracker to Axios clients
const attachGlobalLoadingInterceptors = (client, defaultMsg = "Loading data...") => {
  client.interceptors.request.use(
    (config) => {
      if (!config.skipLoader && !config.silent) {
        triggerGlobalLoadingStart(config.loadingMessage || defaultMsg);
      }
      return config;
    },
    (error) => {
      if (!error?.config?.skipLoader && !error?.config?.silent) {
        triggerGlobalLoadingStop();
      }
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => {
      if (!response?.config?.skipLoader && !response?.config?.silent) {
        triggerGlobalLoadingStop();
      }
      return response;
    },
    (error) => {
      if (!error?.config?.skipLoader && !error?.config?.silent) {
        triggerGlobalLoadingStop();
      }
      return Promise.reject(error);
    }
  );
};

attachGlobalLoadingInterceptors(apiClient, "Loading data...");
attachGlobalLoadingInterceptors(financeApiClient, "Processing salary data...");
attachGlobalLoadingInterceptors(leaveApiClient, "Loading leave data...");

export default apiClient;

